import { useCallback, useMemo, useState } from "react";
import { EmpreendimentoBlocoDropDown } from "../../../incorporacao/domain/models/empreendimento_bloco_dropdown";
import { useLocation, useNavigate } from "react-router";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { ItemServicoEmpreendimento } from "../../../incorporacao/domain/models/item_servico_empreendimento";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useEmpreendimentoItensPagination } from "../hooks/empreendimento_unidade_lista/pagination";
import { useItensChecklistServicoEmpreendimentoFilters } from "../hooks/empreendimento_unidade_itens/filters";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";


type LoadListaItensServicoEmpreendimentoParams = {
    idChecklistServicoEmpreendimento?: number;
    idsChecklistServico?: number[];
    idsBlocos?: number[];
    idsUnidades?: number[];
    idsItemServicoEmpreendimentos?: number[];

    currentPage?: number;
    rowsPerPage?: number;
};

export const useEmpreendimentoUnidadeItensViewModel = () => {
    const {
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        },
        obras: {
            getChecklistServicosDropDownUseCase,
            getObraHeaderInfoUseCase,
        },
        incorporacao: {
            updateItensServicoEmpreendimentoUseCase,
            //createItensServicoEmpreendimentoUseCase,
            getAllItensServicoEmpreendimentoUseCase,
            getEmpreendimentoBlocoDropdownUseCase,

        },
    } = useDependencyInjection();

    const location = useLocation();
    const navigate = useNavigate();
    const backOrNavigate = useBackOrNavigate();

    const pagination = useEmpreendimentoItensPagination();
    const debouncedRef = useCreateDebounceHandler(750);
    const filter = useItensChecklistServicoEmpreendimentoFilters();

    const { pushErrorNotification, pushInfoNotification } = usePageLoader();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [selectedIdEmpreendimento, setSelectedIdEmpreendimento] = useState<number | undefined>(undefined);
    const [selectedIdChecklistServicoEmpreendimento, setSelectedIdChecklistServicoEmpreendimento] = useState<number | undefined>(undefined);
    const [selectedIdObra, setSelectedIdObra] = useState<number | undefined>(undefined);
    const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();

    const obrasTopHeaderState = useObrasTopHeaderState();
    const [obrasReportState, loadObrasCommonReportsData] = useObrasCommonReports({ idObra: selectedIdObra });
    const [listBlocoDropDown, setListBlocoDropDown] = useState<EmpreendimentoBlocoDropDown[]>([])
    const [listUnidadeDropDown, setListUnidadeDropDown] = useState<DropDown[]>([])


    // Estado relacionadas aos serviços do empreendimento
    const [itensServicoEmpreendimento, setItensServicoEmpreendimento] = useState<ItemServicoEmpreendimento[]>([])
    // Valores como vieram do servidor, usados para saber quais itens foram realmente alterados
    const [itensServicoEmpreendimentoOriginal, setItensServicoEmpreendimentoOriginal] = useState<ItemServicoEmpreendimento[]>([])

    // O input de valor dispara onChange ao aplicar a máscara na montagem, então a
    // edição é detectada comparando com o valor original e não pelo evento.
    const alteracoItensServicoEmpreendimento = useMemo(() => {
        const originais = new Map(itensServicoEmpreendimentoOriginal.map(x => [x.idItemServicoEmpreendimento, x]));
        const normalizar = (valor: number | null | undefined) => Math.round((valor ?? 0) * 100);

        return itensServicoEmpreendimento.filter(item => {
            const original = originais.get(item.idItemServicoEmpreendimento);
            if (!original) return false;
            return normalizar(item.quantidade) !== normalizar(original.quantidade)
                || normalizar(item.valorUnitario) !== normalizar(original.valorUnitario);
        });
    }, [itensServicoEmpreendimento, itensServicoEmpreendimentoOriginal]);

    const [checklistServicosDropDown, setChecklistServicosDropDown] = useState<DropDown[]>([])


    //#region CARREGAMENTO
    const loadListaItensServicoEmpreendimento = useCallback(async (funcState: LoadListaItensServicoEmpreendimentoParams) => {
        try {
            const filteredIdChecklistServicoEmpreendimento = funcState.idChecklistServicoEmpreendimento ?? filter.idChecklistServicoEmpreendimento;
            const filteredIdsChecklistServico = funcState.idsChecklistServico ?? filter.idsChecklistServico;
            const filteredIdsBloco = funcState.idsBlocos ?? filter.idsBlocos;
            const filteredIdsUnidade = funcState.idsUnidades ?? filter.idsUnidades;
            const filteredIdsItemServicoEmpreendimento = funcState.idsItemServicoEmpreendimentos ?? filter.idsItemServicoEmpreendimento;

            const filteredPage = funcState?.currentPage ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllItensServicoEmpreendimentoUseCase
                .getAllItensServicoEmpreendimento(
                    filteredIdChecklistServicoEmpreendimento,
                    filteredIdsChecklistServico,
                    filteredIdsBloco,
                    filteredIdsUnidade,
                    filteredIdsItemServicoEmpreendimento,

                    filteredPage,
                    filteredRowsPerPage,
                );

            pagination.setTotalItems(getAllItensServicoEmpreendimentoUseCase.totalItems);

            setItensServicoEmpreendimento(getAllItensServicoEmpreendimentoUseCase.items);
            setItensServicoEmpreendimentoOriginal(getAllItensServicoEmpreendimentoUseCase.items);
            setListUnidadeDropDown(getAllItensServicoEmpreendimentoUseCase.items.reduce((acc: DropDown[], item) => {
                const exists = acc.some(x => x.id === item.idIncorporacaoImovel);
                if (!exists) {
                    acc.push({
                        id: item.idIncorporacaoImovel,
                        title: item.descricaoBloco + " - " + item.descricaoAndar + " - " + item.descricaoUnidade
                    });
                }
                return acc;
            }, []));

        } catch (e: unknown) {
            await pushErrorNotification("Erro", "Falha ao buscar itens de serviço do empreendimento!", { isAutoFinish: true })
        }
    }, [
        filter,
        pagination,
        getAllItensServicoEmpreendimentoUseCase,
        pushErrorNotification,
    ]);

    const loadBlocoDropDown = useCallback(async (idEmpreendimento: number) => {
        try {
            const idEmpreendimentoState = location.state?.idEmpreendimento as number | 0
            const blocos = await getEmpreendimentoBlocoDropdownUseCase.getEmpreendimentoBlocoDropdown(idEmpreendimento ?? idEmpreendimentoState);
            setListBlocoDropDown([...blocos]);

        } catch (e: unknown) {
            throw new Error("Falha ao buscar centro de custo!");
        }
    }, [
        location.state?.idEmpreendimento,
        getEmpreendimentoBlocoDropdownUseCase,
    ]);

    const loadChecklistServicoDropDown = useCallback(async () => {
        try {
            const checklistServicoLista = await getChecklistServicosDropDownUseCase.getChecklistServicosDropDown();
            setChecklistServicosDropDown(
                [...checklistServicoLista.map(x => ({
                    id: x.idChecklistServico,
                    title: x.descricao
                }))]
            );
        } catch (e: unknown) {
            throw new Error("Falha ao buscar checklist de serviço!");
        }
    }, [
        getChecklistServicosDropDownUseCase,
    ]);


    //IMPLEMENTAR O USECASE dropdown de unidades.
    const loadUnidadeDropDown = useCallback(async (funcState: LoadListaItensServicoEmpreendimentoParams) => {
        try {
        } catch (e: unknown) {
            await pushErrorNotification("Erro", "Falha ao buscar itens de serviço do empreendimento!", { isAutoFinish: true })
        }
    }, [
    ]);

    const loadObraHeaderInfo = useCallback(async (idObra?: number) => {
        const filteredIdObra = idObra ?? selectedIdObra;
        if (!filteredIdObra) {
            setSelectedObraHeaderInfo(undefined);
            return;
        }

        try {
            const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
            setSelectedObraHeaderInfo(response ?? undefined);
        } catch (e: unknown) {
            setSelectedObraHeaderInfo(undefined);
            throw new Error('Falha ao buscar dados da obra!');
        }
    }, [
        selectedIdObra,
        getObraHeaderInfoUseCase,
    ]);

    //#endregion





    //#region  PAGE START / USE EFFECT
    usePageStartBootstrap(async () => {
        try {
            const params = new URLSearchParams(location.search);
            const locationIdEmpreendimento = params.get('id');
            const locationIdChecklistServicoEmpreendimento = params.get('idChecklistServicoEmpreendimento');

            const idEmpreendimento = locationIdEmpreendimento && locationIdEmpreendimento.length > 0 ? Number(locationIdEmpreendimento) : undefined;
            const idChecklistServicoEmpreendimento = locationIdChecklistServicoEmpreendimento && locationIdChecklistServicoEmpreendimento.length > 0 ? Number(locationIdChecklistServicoEmpreendimento) : undefined;
            const locationIdObra = params.get('idObra');
            const idObra = locationIdObra && locationIdObra.length > 0 ? Number(locationIdObra) : undefined;

            setSelectedIdObra(idObra);
            await loadObraHeaderInfo(idObra);
            if (idObra) {
                await loadObrasCommonReportsData(idObra);
            }

            if (idEmpreendimento && idChecklistServicoEmpreendimento) {
                setSelectedIdEmpreendimento(idEmpreendimento);
                setSelectedIdChecklistServicoEmpreendimento(idChecklistServicoEmpreendimento);
                filter.setIdChecklistServicoEmpreendimento(idChecklistServicoEmpreendimento);

                await loadChecklistServicoDropDown();
                await loadBlocoDropDown(idEmpreendimento);
                await loadListaItensServicoEmpreendimento({
                    idChecklistServicoEmpreendimento: idChecklistServicoEmpreendimento,
                    idsChecklistServico: [],
                    idsBlocos: [],
                    idsUnidades: []
                });

                //PRECISA IMPLEMENTAR O USECASE.
                await loadUnidadeDropDown({
                    idsChecklistServico: [idChecklistServicoEmpreendimento],
                    idsBlocos: [],
                    idsUnidades: []
                });

            }
        } catch (e: unknown) {
            throw new Error("Falha ao buscar dados da pagina!");
        }
    });

    // useEffect(() => {
    //     if (salvarPorBloco === 0) {
    //         hookForm.setValue("salvarPorBloco", 0);
    //     } else {
    //         hookForm.setValue("salvarPorBloco", 1);
    //     }
    // }, [salvarPorBloco, hookForm]);

    const handlePageBack = useCallback(() => {
        const idObra = new URLSearchParams(location.search).get('idObra');
        const obraParam = idObra ? `&idObra=${idObra}` : '';
        navigate(`/EmpreendimentoUnidade_cadastro?id=${selectedIdEmpreendimento}${obraParam}`)
    }, [navigate, location.search, selectedIdEmpreendimento])

    //#endregion




    //#region ITENS SERVICO EMPREENDIMENTO
    // const handleCadastroItensEmpreendimentoSubmit = useCallback(async (form: ItensServicoEmpreendimentoForm) => {
    //     try {
    //         if (!form.idChecklistServico || form.idChecklistServico === 0) {
    //             pushErrorNotification("ERRO", ["Selecione pelo menos um checklist de serviço."], { isAutoFinish: true });
    //             return;
    //         }
    //         if (form.salvarPorBloco === 0) {
    //             if (!form.idsBloco || form.idsBloco.length === 0) {
    //                 pushErrorNotification("ERRO", ["Selecione pelo menos um bloco."], { isAutoFinish: true });
    //                 return;
    //             }
    //         } else {
    //             if (!form.idsUnidade || form.idsUnidade.length === 0) {
    //                 pushErrorNotification("ERRO", ["Selecione pelo menos uma unidade."], { isAutoFinish: true });
    //                 return;
    //             }
    //         }
    //         const listaIds = await createItensServicoEmpreendimentoUseCase.createItensServicoEmpreendimento(
    //             form.idsBloco ?? [],
    //             form.idsUnidade ?? [],
    //             form.idChecklistServico,
    //             form.quantidade ?? 0,
    //             form.valorUnitario ?? 0,
    //             form.salvarPorBloco,
    //         );

    //         filter.setIdChecklistServico(form.idChecklistServico);
    //         filter.setIdsBlocos(form.idsBloco ?? []);
    //         filter.setIdsUnidades([]);

    //         await loadListaItensServicoEmpreendimento([], [], [], listaIds);

    //     } catch (e: unknown) {
    //         pushErrorNotification("ERRO", ["Falha ao submeter serviço do empreendimento!"], { isAutoFinish: true });
    //     }
    // }, [
    //     createItensServicoEmpreendimentoUseCase,
    //     filter,
    //     loadListaChecklistServicoEmpreendimento,
    //     loadListaItensServicoEmpreendimento,
    //     pushErrorNotification,
    // ]);

    const handleUpdateItensServicoEmpreendimento = useCallback(async () => {
        if (alteracoItensServicoEmpreendimento.length === 0) {
            pushInfoNotification("AVISO", "Nenhum item foi alterado.", { isAutoFinish: true });
            return;
        }

        try {
            setSaving(true);
            const itensEnviados = alteracoItensServicoEmpreendimento;
            await updateItensServicoEmpreendimentoUseCase.updateItensServicoEmpreendimento(
                itensEnviados
            );

            const enviadosPorId = new Map(itensEnviados.map(x => [x.idItemServicoEmpreendimento, x]));
            setItensServicoEmpreendimentoOriginal((prevState) => prevState.map(x =>
                enviadosPorId.get(x.idItemServicoEmpreendimento) ?? x));
            pushInfoNotification("SUCESSO", "Serviço do empreendimento atualizado com sucesso!", { isAutoFinish: true });
        } catch (e: unknown) {
            pushErrorNotification("ERRO", ["Falha ao atualizar serviço do empreendimento!"], { isAutoFinish: true });
        } finally {
            setSaving(false);
        }

    }, [
        updateItensServicoEmpreendimentoUseCase,
        alteracoItensServicoEmpreendimento,
        pushErrorNotification,
        pushInfoNotification,
    ]);

    const handleQuantidadeChange = useCallback((idItemServicoEmpreendimento: number, newValue: number | null) => {
        setItensServicoEmpreendimento((prevState) => {
            return prevState.map((item): ItemServicoEmpreendimento => {
                if (item.idItemServicoEmpreendimento === idItemServicoEmpreendimento) {
                    return {
                        ...item,
                        quantidade: newValue !== null ? Number(newValue) : null,
                    };
                }
                return item;
            });
        });
    }, []);

    const handleValorChange = useCallback((idItemServicoEmpreendimento: number, newValue: number | null) => {
        setItensServicoEmpreendimento((prevState) => {
            return prevState.map((item): ItemServicoEmpreendimento => {
                if (item.idItemServicoEmpreendimento === idItemServicoEmpreendimento) {
                    return {
                        ...item,
                        valorUnitario: newValue !== null ? Number(newValue) : null,
                    };
                }
                return item;
            });
        });
    }, []);

    //#endregion

    //#region PESQUISA DE ITENS

    const handleIdsBlocoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filter.setIdsBlocos(newValues ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadListaItensServicoEmpreendimento({
                        idsBlocos: newValues,
                        currentPage: 1,
                    });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar novos itens de serviço do empreendimento!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [
            filter,
            pagination,
            loadListaItensServicoEmpreendimento,
            pushErrorNotification
        ]
    );

    const handleIdsChecklistServicoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filter.setIdsChecklistServico(newValues ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadListaItensServicoEmpreendimento({
                        idsChecklistServico: newValues,
                        currentPage: 1,
                    });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar novos itens de serviço do empreendimento!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [
            filter,
            pagination,
            loadListaItensServicoEmpreendimento,
            pushErrorNotification
        ]
    );

    const handleIdsUnidadeChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filter.setIdsUnidades(newValues ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadListaItensServicoEmpreendimento({
                        idsUnidades: newValues,
                        currentPage: 1,
                    });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar novos itens de serviço do empreendimento!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [
            filter,
            pagination,
            loadListaItensServicoEmpreendimento,
            pushErrorNotification
        ]
    );

    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filter.setIdsBlocos([]);
            filter.setIdsUnidades([]);
            pagination.setPage(1);
            await loadListaItensServicoEmpreendimento({
                idsBlocos: [],
                idsUnidades: [],
                currentPage: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar itens de serviço do empreendimento!'));
        } finally {
            setLoading(false);
        }
    }, [
        filter,
        pagination,
        loadListaItensServicoEmpreendimento,
        pushErrorNotification,
    ]);

    //#endregion

    //#region PAGINAÇÃO.
    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            pagination.setPage(1);
            await Promise.all([
                loadObraHeaderInfo(),
                selectedIdObra ? loadObrasCommonReportsData() : Promise.resolve(),
                loadListaItensServicoEmpreendimento({
                    currentPage: 1,
                }),
            ]);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar itens de serviço do empreendimento!'));
        } finally {
            setLoading(false);
        }
    }, [
        pagination,
        selectedIdObra,
        loadObraHeaderInfo,
        loadObrasCommonReportsData,
        loadListaItensServicoEmpreendimento,
        pushErrorNotification
    ]);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadListaItensServicoEmpreendimento({
                currentPage: page + 1,
            });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar itens de serviço do empreendimento!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadListaItensServicoEmpreendimento, pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadListaItensServicoEmpreendimento({
                currentPage: 1,
                rowsPerPage,
            });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar itens de serviço do empreendimento!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadListaItensServicoEmpreendimento, pushErrorNotification]);

    //#endregion


    //#region HISTORY

    // const handleOpenChangeHistory = useCallback((
    //     idTabela: number | null,
    //     tipoTabela: LogTipoTabela | null,
    // ) => {
    //     if (idTabela && tipoTabela) {
    //         changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    //     } else {
    //         if (selectedIdEmpreendimento) {
    //             changeHistory.handleOpenChangeHistory(selectedIdEmpreendimento, LogTipoTabela.EMPREENDIMENTO, true);
    //         }
    //     }
    // }, [
    //     selectedIdEmpreendimento,
    //     changeHistory,
    // ]);

    //#endregion

    return {
        saving,
        loading,
        changeHistory,
        itensServicoEmpreendimento,
        possuiAlteracaoItens: alteracoItensServicoEmpreendimento.length > 0,
        listBlocoDropDown,
        listUnidadeDropDown: listUnidadeDropDown ?? [],
        checklistServicosDropDown: checklistServicosDropDown ?? [],
        filters: filter,
        selectedIdChecklistServicoEmpreendimento,
        selectedObraHeaderInfo: selectedObraHeaderInfo ?? null,
        obrasTopHeaderState,
        obrasReportState,
        pagination,

        handleClearFilters,
        //handleCadastroItensEmpreendimentoSubmit: hookForm.handleSubmit(handleCadastroItensEmpreendimentoSubmit),
        handleIdsChecklistServicoChange,
        handleIdsBlocoChange,
        handleUpdateItensServicoEmpreendimento,
        setListBlocoDropDown,
        handlePageBack,

        handleQuantidadeChange,
        handleValorChange,

        handleIdsUnidadeChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
    };
}

